/**
 * AdminApiPage — Network-level admin API surface.
 *
 * Sections:
 *   1. Header — green accent, light theme.
 *   2. Rate-limit probe — current tenant budget + remaining (refetches every 15s).
 *   3. Endpoint catalog — which routes are admin-API surface, by method/scope.
 *   4. API keys — issue + list + revoke, with one-time raw reveal.
 *   5. Access log — recent tenant-scoped audit rows.
 *
 * GREEN-only branding. Light theme only. No emerald/sky/blue/orange/purple,
 * no dark mode, no emoji, no competitor refs.
 */
import { useState, type FormEvent } from "react";
import {
	ADMIN_SCOPES,
	type AdminEndpoint,
	type AdminScope,
} from "./api";
import {
	useAdminAccessLog,
	useAdminEndpoints,
	useAdminKeyCreate,
	useAdminKeyRevoke,
	useAdminKeys,
	useAdminRateLimit,
} from "./hooks";

// --- Tailwind tokens (GREEN-only, light theme) -----------------------------

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const BTN_PRIMARY =
	"rounded-[6px] border border-green-700 bg-green-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50";
const BTN_DANGER =
	"rounded-[6px] border border-red-300 bg-white px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";
const HEADLINE =
	"bg-gradient-to-r from-green-700 to-green-500 bg-clip-text text-3xl font-semibold text-transparent";
const SECTION_LABEL =
	"text-xs font-semibold uppercase tracking-wider text-zinc-500";
const MONO_BOX =
	"rounded-[6px] border border-zinc-200 bg-zinc-50 p-3 text-xs font-mono text-zinc-800 whitespace-pre-wrap break-all";
const BADGE =
	"inline-flex items-center rounded-[6px] border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide";
const BADGE_METHOD: Record<AdminEndpoint["method"], string> = {
	GET: "border-green-200 bg-green-50 text-green-800",
	POST: "border-zinc-200 bg-zinc-50 text-zinc-800",
	PUT: "border-zinc-200 bg-zinc-50 text-zinc-800",
	PATCH: "border-zinc-200 bg-zinc-50 text-zinc-800",
	DELETE: "border-red-200 bg-red-50 text-red-700",
};

// --- Inline SVG icons ------------------------------------------------------

const IconKey = ({ className }: { className?: string }) => (
	<svg
		aria-hidden="true"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth={1.75}
		strokeLinecap="round"
		strokeLinejoin="round"
		className={className}
	>
		<circle cx="7.5" cy="15.5" r="3.5" />
		<path d="M11 12 20 3" />
		<path d="M17 6l3 3" />
		<path d="M15 8l3 3" />
	</svg>
);

const IconGauge = ({ className }: { className?: string }) => (
	<svg
		aria-hidden="true"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth={1.75}
		strokeLinecap="round"
		strokeLinejoin="round"
		className={className}
	>
		<path d="M3 12a9 9 0 0 1 18 0" />
		<path d="M12 12l4-3" />
		<circle cx="12" cy="12" r="1.2" fill="currentColor" />
	</svg>
);

const IconList = ({ className }: { className?: string }) => (
	<svg
		aria-hidden="true"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth={1.75}
		strokeLinecap="round"
		strokeLinejoin="round"
		className={className}
	>
		<path d="M4 6h16M4 12h16M4 18h16" />
	</svg>
);

const IconBook = ({ className }: { className?: string }) => (
	<svg
		aria-hidden="true"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth={1.75}
		strokeLinecap="round"
		strokeLinejoin="round"
		className={className}
	>
		<path d="M4 5a2 2 0 0 1 2-2h12v16H6a2 2 0 0 0-2 2V5z" />
		<path d="M18 3v16" />
	</svg>
);

// --- Helpers ---------------------------------------------------------------

function formatTs(sec: number): string {
	try {
		return new Date(sec * 1000).toISOString().replace("T", " ").slice(0, 19);
	} catch {
		return String(sec);
	}
}

// --- Component -------------------------------------------------------------

export function AdminApiPage() {
	const endpointsQ = useAdminEndpoints();
	const keysQ = useAdminKeys();
	const logQ = useAdminAccessLog(50);
	const rateQ = useAdminRateLimit();
	const createMut = useAdminKeyCreate();
	const revokeMut = useAdminKeyRevoke();

	const [name, setName] = useState("");
	const [scopes, setScopes] = useState<AdminScope[]>(["campaigns.read"]);
	const [revealed, setRevealed] = useState<string | null>(null);
	const [revealedName, setRevealedName] = useState<string | null>(null);

	const toggleScope = (s: AdminScope) => {
		setScopes((cur) =>
			cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s],
		);
	};

	const handleIssue = (e: FormEvent) => {
		e.preventDefault();
		if (!name.trim() || scopes.length === 0) return;
		createMut.mutate(
			{ name: name.trim(), scopes },
			{
				onSuccess: (data) => {
					setRevealed(data.raw_key);
					setRevealedName(data.key.name);
					setName("");
				},
			},
		);
	};

	const rate = rateQ.data;
	const ratePct = rate
		? Math.min(
				100,
				Math.round((rate.used_current_window / Math.max(1, rate.limit_per_minute)) * 100),
		  )
		: 0;

	const keys = keysQ.data?.keys ?? [];
	const endpoints = endpointsQ.data?.endpoints ?? [];
	const logEntries = logQ.data?.entries ?? [];

	return (
		<main
			data-testid="admin-api-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10 font-sans text-zinc-900"
		>
			{/* Header */}
			<header className="flex flex-col gap-2">
				<p className={SECTION_LABEL}>network admin</p>
				<h1 className={HEADLINE}>Admin API</h1>
				<p className="max-w-2xl text-sm text-zinc-600">
					Manage network-level API keys, inspect the admin endpoint catalog,
					review access-log history, and monitor the current rate-limit budget.
				</p>
			</header>

			{/* Rate-limit probe */}
			<section className={CARD} aria-label="Rate limit">
				<div className="flex items-center gap-2">
					<IconGauge className="h-4 w-4 text-green-700" />
					<h2 className="text-base font-semibold">Rate limit</h2>
				</div>
				{rateQ.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="admin-api-rate-loading"
					>
						Loading.
					</p>
				) : rateQ.error ? (
					<p className="mt-3 text-sm text-red-600" role="alert">
						{rateQ.error.message}
					</p>
				) : rate ? (
					<div className="mt-3 flex flex-col gap-2" data-testid="admin-api-rate">
						<div className="flex items-baseline justify-between text-sm">
							<span className="text-zinc-700">
								<span className="font-semibold text-zinc-900">
									{rate.used_current_window}
								</span>{" "}
								/ {rate.limit_per_minute} used
							</span>
							<span className="text-xs text-zinc-500">
								window: {rate.window_seconds}s — remaining {rate.remaining}
							</span>
						</div>
						<div
							className="h-2 w-full overflow-hidden rounded-[6px] bg-zinc-100"
							role="progressbar"
							aria-valuenow={ratePct}
							aria-valuemin={0}
							aria-valuemax={100}
						>
							<div
								className="h-full rounded-[6px] bg-green-500 transition-[width]"
								style={{ width: `${ratePct}%` }}
							/>
						</div>
					</div>
				) : null}
			</section>

			{/* Endpoints catalog */}
			<section className={CARD} aria-label="Endpoint catalog">
				<div className="flex items-center gap-2">
					<IconBook className="h-4 w-4 text-green-700" />
					<h2 className="text-base font-semibold">Endpoint catalog</h2>
				</div>
				{endpointsQ.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="admin-api-endpoints-loading"
					>
						Loading.
					</p>
				) : endpointsQ.error ? (
					<p className="mt-3 text-sm text-red-600" role="alert">
						{endpointsQ.error.message}
					</p>
				) : endpoints.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="admin-api-endpoints-empty"
					>
						No endpoints registered.
					</p>
				) : (
					<ul
						className="mt-3 divide-y divide-zinc-200"
						data-testid="admin-api-endpoints"
					>
						{endpoints.map((ep) => (
							<li
								key={`${ep.method}-${ep.path}`}
								className="flex flex-col gap-1 py-2 sm:flex-row sm:items-center sm:justify-between"
							>
								<div className="flex min-w-0 items-center gap-3">
									<span className={`${BADGE} ${BADGE_METHOD[ep.method]}`}>
										{ep.method}
									</span>
									<code className="truncate font-mono text-xs text-zinc-900">
										{ep.path}
									</code>
								</div>
								<div className="flex items-center gap-3 text-xs text-zinc-600">
									<span className="truncate">{ep.description}</span>
									<span className={`${BADGE} border-green-200 bg-green-50 text-green-800 normal-case tracking-normal`}>
										{ep.scope}
									</span>
								</div>
							</li>
						))}
					</ul>
				)}
			</section>

			{/* Issue key */}
			<section className={CARD} aria-label="Issue API key">
				<div className="flex items-center gap-2">
					<IconKey className="h-4 w-4 text-green-700" />
					<h2 className="text-base font-semibold">Issue API key</h2>
				</div>
				<form
					onSubmit={handleIssue}
					className="mt-3 flex flex-col gap-4"
					data-testid="admin-api-issue-form"
				>
					<div className="flex flex-col gap-1">
						<label
							htmlFor="admin-api-name"
							className="text-xs font-medium text-zinc-700"
						>
							Key name
						</label>
						<input
							id="admin-api-name"
							type="text"
							className={INPUT}
							placeholder="e.g. analytics-read-only"
							value={name}
							onChange={(e) => setName(e.currentTarget.value)}
							disabled={createMut.isPending}
							maxLength={64}
							data-testid="admin-api-name-input"
						/>
					</div>
					<div className="flex flex-col gap-1">
						<span className="text-xs font-medium text-zinc-700">Scopes</span>
						<div
							className="grid grid-cols-1 gap-2 sm:grid-cols-2"
							data-testid="admin-api-scopes"
						>
							{ADMIN_SCOPES.map((s) => {
								const checked = scopes.includes(s);
								return (
									<label
										key={s}
										className={`flex cursor-pointer items-center gap-2 rounded-[6px] border px-2.5 py-1.5 text-sm ${
											checked
												? "border-green-600 bg-green-50 text-green-900"
												: "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300"
										}`}
									>
										<input
											type="checkbox"
											className="h-3.5 w-3.5 accent-green-600"
											checked={checked}
											onChange={() => toggleScope(s)}
											data-testid={`admin-api-scope-${s}`}
										/>
										<span className="font-mono text-xs">{s}</span>
									</label>
								);
							})}
						</div>
					</div>
					<div className="flex items-center gap-3">
						<button
							type="submit"
							className={BTN_PRIMARY}
							disabled={
								createMut.isPending || !name.trim() || scopes.length === 0
							}
							data-testid="admin-api-issue-submit"
						>
							{createMut.isPending ? "Issuing." : "Issue key"}
						</button>
						{createMut.error ? (
							<span className="text-xs text-red-600" role="alert">
								{createMut.error.message}
							</span>
						) : null}
					</div>
				</form>
				{revealed ? (
					<div
						className="mt-4 flex flex-col gap-2 rounded-[6px] border border-green-300 bg-green-50 p-3"
						data-testid="admin-api-revealed"
					>
						<p className="text-xs font-semibold uppercase tracking-wider text-green-900">
							One-time reveal
							{revealedName ? ` — ${revealedName}` : ""}
						</p>
						<div className={MONO_BOX}>{revealed}</div>
						<div className="flex items-center gap-2">
							<button
								type="button"
								className={BTN_GHOST}
								onClick={() => {
									setRevealed(null);
									setRevealedName(null);
								}}
								data-testid="admin-api-dismiss-reveal"
							>
								Dismiss
							</button>
							<span className="text-[11px] text-zinc-600">
								Copy now. The raw secret cannot be retrieved again.
							</span>
						</div>
					</div>
				) : null}
			</section>

			{/* Keys list */}
			<section className={CARD} aria-label="API keys">
				<div className="flex items-center gap-2">
					<IconList className="h-4 w-4 text-green-700" />
					<h2 className="text-base font-semibold">API keys</h2>
				</div>
				{keysQ.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="admin-api-keys-loading"
					>
						Loading.
					</p>
				) : keysQ.error ? (
					<p className="mt-3 text-sm text-red-600" role="alert">
						{keysQ.error.message}
					</p>
				) : keys.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="admin-api-empty"
					>
						No keys yet. Issue one above.
					</p>
				) : (
					<div className="mt-3 overflow-x-auto" data-testid="admin-api-keys">
						<table className="w-full min-w-[640px] text-left text-sm">
							<thead>
								<tr className="border-b border-zinc-200 text-xs uppercase tracking-wide text-zinc-500">
									<th className="py-2 pr-3 font-medium">Name</th>
									<th className="py-2 pr-3 font-medium">Preview</th>
									<th className="py-2 pr-3 font-medium">Scopes</th>
									<th className="py-2 pr-3 font-medium">Status</th>
									<th className="py-2 pr-3 font-medium">Created</th>
									<th className="py-2 pr-3 font-medium" />
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-100">
								{keys.map((k) => (
									<tr key={k.id} data-testid={`admin-api-row-${k.id}`}>
										<td className="py-2 pr-3 text-zinc-900">{k.name}</td>
										<td className="py-2 pr-3 font-mono text-xs text-zinc-700">
											{k.key_preview}
										</td>
										<td className="py-2 pr-3">
											<span className="flex flex-wrap gap-1">
												{k.scopes.map((s) => (
													<span
														key={s}
														className={`${BADGE} border-green-200 bg-green-50 text-green-800 normal-case tracking-normal`}
													>
														{s}
													</span>
												))}
											</span>
										</td>
										<td className="py-2 pr-3">
											{k.active ? (
												<span className={`${BADGE} border-green-300 bg-green-50 text-green-900`}>
													Active
												</span>
											) : (
												<span className={`${BADGE} border-zinc-200 bg-zinc-50 text-zinc-600`}>
													Revoked
												</span>
											)}
										</td>
										<td className="py-2 pr-3 text-xs text-zinc-600">
											{formatTs(k.created_at)}
										</td>
										<td className="py-2 pr-3 text-right">
											{k.active ? (
												<button
													type="button"
													className={BTN_DANGER}
													disabled={revokeMut.isPending}
													onClick={() => {
														if (
															window.confirm(
																`Revoke key "${k.name}"? This cannot be undone.`,
															)
														) {
															revokeMut.mutate(k.id);
														}
													}}
													data-testid={`admin-api-revoke-${k.id}`}
												>
													Revoke
												</button>
											) : k.revoked_at ? (
												<span className="text-[11px] text-zinc-500">
													{formatTs(k.revoked_at)}
												</span>
											) : null}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
				{revokeMut.error ? (
					<p className="mt-2 text-xs text-red-600" role="alert">
						{revokeMut.error.message}
					</p>
				) : null}
			</section>

			{/* Access log */}
			<section className={CARD} aria-label="Access log">
				<div className="flex items-center gap-2">
					<IconList className="h-4 w-4 text-green-700" />
					<h2 className="text-base font-semibold">Access log</h2>
				</div>
				{logQ.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="admin-api-log-loading"
					>
						Loading.
					</p>
				) : logQ.error ? (
					<p className="mt-3 text-sm text-red-600" role="alert">
						{logQ.error.message}
					</p>
				) : logEntries.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="admin-api-log-empty"
					>
						No access-log entries yet.
					</p>
				) : (
					<div className="mt-3 overflow-x-auto" data-testid="admin-api-log">
						<table className="w-full min-w-[640px] text-left text-sm">
							<thead>
								<tr className="border-b border-zinc-200 text-xs uppercase tracking-wide text-zinc-500">
									<th className="py-2 pr-3 font-medium">Time</th>
									<th className="py-2 pr-3 font-medium">Method</th>
									<th className="py-2 pr-3 font-medium">Path</th>
									<th className="py-2 pr-3 font-medium">Status</th>
									<th className="py-2 pr-3 font-medium">Key</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-100">
								{logEntries.map((r) => (
									<tr key={r.id} data-testid={`admin-api-log-row-${r.id}`}>
										<td className="py-1.5 pr-3 font-mono text-xs text-zinc-700">
											{formatTs(r.ts)}
										</td>
										<td className="py-1.5 pr-3">
											<span
												className={`${BADGE} ${
													BADGE_METHOD[
														(r.method as AdminEndpoint["method"]) ?? "GET"
													] ?? BADGE_METHOD.GET
												}`}
											>
												{r.method}
											</span>
										</td>
										<td className="py-1.5 pr-3 font-mono text-xs text-zinc-900">
											{r.path}
										</td>
										<td className="py-1.5 pr-3 font-mono text-xs text-zinc-700">
											{r.status_code}
										</td>
										<td className="py-1.5 pr-3 font-mono text-[11px] text-zinc-500">
											{r.key_id ?? "-"}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</section>
		</main>
	);
}

export default AdminApiPage;
